/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import { PrimaryButton } from '../../../../../styles/ui/Button';
import { updateManualSteps } from '../../../../../services/hr/recruitment/onboardingApi';
import { ONBOARDING_STATUS } from '../../constants';
import { recruitmentErrorMessage } from '../../utils/apiError';
import {
   hasManualFields,
   manualStepDraft,
   manualStepPatch,
} from '../manualSteps';
import { StepPanel } from '../styles';
import ManualStepFields from './ManualStepFields';
import ExamDocumentField from './ExamDocumentField';

// ---------------------------------------------------------------------------
// Painel de trabalho da ETAPA ATIVA do pipeline (design 2026-07-14: os passos
// manuais aparecem na própria etapa, quando ela fica ativa — não antes).
// Renderiza dentro do card do pipeline, entre o stepper e o banner. Etapa sem
// campos manuais (coleta, pré-cadastro, aguardando início) → não renderiza.
// ---------------------------------------------------------------------------

export default function StepWorkPanel({ onboarding, canUpdate, onSaved }) {
   const stepKey = onboarding?.status;
   const baseline = useMemo(
      () => manualStepDraft(stepKey, onboarding),
      [stepKey, onboarding],
   );
   const [draft, setDraft] = useState(baseline);
   const [isSaving, setIsSaving] = useState(false);

   // Reset do rascunho por VALOR (não por identidade do objeto): um refetch
   // que não mudou os campos (ex.: upload do laudo) não apaga edição não-salva.
   const baselineJson = JSON.stringify(baseline);
   useEffect(() => {
      setDraft(JSON.parse(baselineJson));
   }, [baselineJson]);

   if (!hasManualFields(stepKey)) return null;

   const isDirty = Object.keys(baseline).some(
      (key) => draft[key] !== baseline[key],
   );

   const handleSave = async () => {
      if (isSaving || !isDirty) return;
      setIsSaving(true);
      try {
         const { message } = await updateManualSteps(
            onboarding.id,
            manualStepPatch(stepKey, draft, baseline),
         );
         toast.success(message);
         onSaved?.();
      } catch (err) {
         toast.error(
            recruitmentErrorMessage(err, 'Erro ao salvar o passo manual.'),
         );
      } finally {
         setIsSaving(false);
      }
   };

   return (
      <StepPanel>
         <ManualStepFields
            stepKey={stepKey}
            draft={draft}
            onChange={(key, value) =>
               setDraft((prev) => ({ ...prev, [key]: value }))
            }
            disabled={!canUpdate || isSaving}
            idPrefix="step-panel"
         >
            {stepKey === ONBOARDING_STATUS.EXAME_ADMISSIONAL && (
               <ExamDocumentField
                  onboarding={onboarding}
                  canAct={canUpdate && !isSaving}
                  onChanged={onSaved}
               />
            )}
         </ManualStepFields>
         {canUpdate && (
            <div
               style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: 12,
               }}
            >
               <PrimaryButton
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving || !isDirty}
               >
                  {isSaving ? 'Salvando…' : 'Salvar passo'}
               </PrimaryButton>
            </div>
         )}
      </StepPanel>
   );
}

StepWorkPanel.propTypes = {
   onboarding: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.string,
   }),
   canUpdate: PropTypes.bool,
   onSaved: PropTypes.func,
};
